<script lang="ts">
  import { mdiAutoFix, mdiCancel } from "@mdi/js"
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiPencil, mdiSync } from "@mdi/js"
  import ProvisioningTemplateOverlay, {
    type ProjectTemplatePayload,
    type RealizationTemplatePayload,
  } from "./_modules/provisioning-template-overlay"
  import { resolveGraphType } from "../../../../../../../../../../../../_shared/_logic/project-features"
  import {
    applyGraphRealizationsToProjects,
    applyProjectPayload,
    applyRealizationPayload,
    buildProjectsPayload,
    createDefaultProjectTemplate,
    normalizeGraphRealizations,
    normalizeProjectState,
    normalizeProjectTemplate,
  } from "../../../../_shared/_logic/helpers"
  import type { GraphRealizationDefinition } from "../../../../_shared/_logic/helpers"
  import type { TemplateProject } from "../../../../_shared/_logic/provisioning-contract"
  import {
    normalizeSessionConfig,
    SESSION_DEFAULTS_INACTIVE,
  } from "../../../../../../../../../../../../_shared/_logic/session-configuration"
  import type { GraphViewFrameContext } from "../../../../_shared/_logic/graph-contract"
  import type { GraphProvisioningPageContext as PageContext } from "../../../../_shared/_logic/page-context"

  let { viewFrameContext: _viewFrameContext, pageContext: pc } = $props<{
    viewFrameContext: GraphViewFrameContext
    pageContext: PageContext
  }>()

  const loadError = $derived(
    pc.error ?? (!pc.graph || !pc.draft ? "Graph konnte nicht geladen werden." : null),
  )

  let graphRealizations = $state<GraphRealizationDefinition[]>([])
  let projectTemplates = $state<TemplateProject[]>([])
  let hydrated = $state(false)
  let autoApplySelection = $state<"true" | "false">("false")

  const AUTO_APPLY_TOGGLE_OPTIONS = [
    { value: "true", label: "Automatisch anwenden", icon: mdiAutoFix },
    { value: "false", label: "Nicht anwenden", icon: mdiCancel },
  ]

  const graphType = $derived(resolveGraphType(pc.draft ?? pc.graph))
  const hasVoiceFeature = $derived(graphType === "voicebot")
  const hasChatFeature = $derived(graphType === "chatbot")

  const shouldShowTemplate = $derived.by(() => autoApplySelection === "true")
  const templateValid = $derived.by(() => {
    if (!shouldShowTemplate) return true

    if (!projectTemplates.length) return false

    return projectTemplates.every((project) => {
      const hasBasics = Boolean(project.name.trim() && project.slug.trim())
      const hasRealizations = project.realizations.length > 0
      const realizationsValid = project.realizations.every(
        (real) => Boolean(real.name.trim()) && Boolean(real.slug.trim()),
      )

      return hasBasics && hasRealizations && realizationsValid
    })
  })

  type LanguageRow = DataRow & {
    name: string
    flag: string
    slug: string
  }

  const languageColumns: ColumnDef[] = [
    { id: "name", label: "Sprache", isPrimary: true },
    { id: "flag", label: "Flagge", width: "120px" },
    { id: "slug", label: "Kürzel", width: "140px" },
  ]

  const languageRows: LanguageRow[] = $derived.by(() => {
    const project = projectTemplates[0] ?? null
    const realizationBySlug = new Map(
      (project?.realizations ?? []).map((real) => [real.slug, real]),
    )

    return graphRealizations.map((entry) => {
      const realization = realizationBySlug.get(entry.slug) ?? null

      return {
        id: entry.slug,
        name: entry.name || entry.slug.toUpperCase(),
        flag: entry.flag ?? "",
        slug: entry.slug,
      }
    })
  })

  const languageRefreshKey = $derived.by(() => {
    const languageKey = graphRealizations
      .map((entry) => `${entry.slug}:${entry.name}:${entry.flag ?? ""}`)
      .join("|")
    const project = projectTemplates[0] ?? null
    const realizationKey = project
      ? project.realizations.map((real) => `${real.slug}:${real.status}:${real.locked}`).join("|")
      : "none"

    return `${languageKey}::${realizationKey}`
  })

  const getLanguageRows: TableGetRows = ({ limit, offset }) => {
    const start = Math.max(0, offset ?? 0)
    const size = typeof limit === "number" && limit > 0 ? limit : languageRows.length
    const end = start + size

    return { rows: languageRows.slice(start, end), total: languageRows.length }
  }

  const extractTemplateMeta = (value: unknown) => {
    const tpl = (value ?? {}) as Record<string, unknown>

    return Object.fromEntries(
      Object.entries(tpl).filter(([key]) => key !== "projects" && key !== "realizations"),
    )
  }

  const commitProvisioningTemplate = () => {
    const draft = pc.draft

    if (!draft || !hydrated) return

    const projectsPayload = buildProjectsPayload(projectTemplates)
    draft.autoApply = autoApplySelection === "true"
    const meta = extractTemplateMeta(draft.provisioningTemplate)
    draft.provisioningTemplate =
      projectsPayload.length || Object.keys(meta).length
        ? { ...meta, projects: projectsPayload }
        : {}
  }

  const saveProvisioning = async () => {
    if (pc.saving || pc.synchronizing) return

    const draft = pc.draft

    if (!draft || !hydrated) return

    commitProvisioningTemplate()

    if (!templateValid) return

    try {
      const saveResult = await pc.onSave?.()

      if (saveResult === false) return

      if (shouldShowTemplate) {
        await pc.onSynchronize?.()
      }
    } catch (err) {
      console.error("graph_provisioning_save_failed", err)
    }
  }

  const applyProjects = (updater: (projects: TemplateProject[]) => TemplateProject[]) => {
    if (pc.saving || pc.synchronizing) return

    const next = updater(projectTemplates)
    const normalized = next.map((project) => normalizeProjectState(project))
    projectTemplates = applyGraphRealizationsToProjects(normalized, graphRealizations)
    void saveProvisioning()
  }

  const hydrateFromDraft = () => {
    const draft = pc.draft

    if (!draft) return

    hydrated = false
    autoApplySelection = draft.autoApply ? "true" : "false"
    const tpl = (draft.provisioningTemplate ?? {}) as Record<string, unknown>
    const normalizedGraphRealizations = normalizeGraphRealizations(draft.realizations)
    graphRealizations = normalizedGraphRealizations
    const projects = Array.isArray((tpl as { projects?: unknown }).projects) ? tpl.projects : []
    const normalizedProjects = (projects as Record<string, unknown>[]).map((project, index) =>
      normalizeProjectTemplate(project, index, { fallbackProjectName: pc.graph?.label }),
    )
    projectTemplates = applyGraphRealizationsToProjects(
      normalizedProjects,
      normalizedGraphRealizations,
    )
    hydrated = true
    commitProvisioningTemplate()
  }

  onMount(hydrateFromDraft)

  const handleSynchronizeClick = async () => {
    if (pc.saving || pc.synchronizing) return

    if (!shouldShowTemplate || !templateValid) return

    await pc.onSynchronize?.()
  }

  const handleAutoApplyChange = () => {
    if (pc.saving || pc.synchronizing) return

    void saveProvisioning()
  }

  const handleAutoApplyToggle = (value: string) => {
    if (pc.saving || pc.synchronizing) return

    autoApplySelection = (value as "true" | "false") ?? autoApplySelection

    if (autoApplySelection === "true" && !projectTemplates.length) {
      const baseName = pc.graph?.label ?? "Projekt"
      applyProjects(() => [createDefaultProjectTemplate(baseName)])

      return
    }

    handleAutoApplyChange()
  }

  const handleProjectSubmit = (target: TemplateProject | null, payload: ProjectTemplatePayload) => {
    applyProjects((projects) => {
      const targetId = target?.id ?? null
      const payloadKey = payload.key != null ? String(payload.key) : null
      const payloadSlug = (payload.slug ?? "").trim()
      const matchIndex = projects.findIndex((project) => {
        if (targetId && project.id === targetId) return true

        if (payloadKey && project.key != null && String(project.key) === payloadKey) return true

        if (payloadSlug && project.slug === payloadSlug) return true

        return false
      })

      if (matchIndex >= 0) {
        return projects.map((project, idx) =>
          idx === matchIndex
            ? applyProjectPayload(project, payload, { fallbackProjectName: pc.graph?.label })
            : project,
        )
      }

      return [
        ...projects,
        applyProjectPayload(null, payload, { fallbackProjectName: pc.graph?.label }),
      ]
    })
  }

  const handleRealizationSubmit = (languageSlug: string, payload: RealizationTemplatePayload) => {
    applyProjects((projects) => {
      if (!projects.length) return projects

      const project = projects[0]
      const current = project.realizations.find((real) => real.slug === languageSlug) ?? null
      const languageDefinition =
        graphRealizations.find((entry) => entry.slug === languageSlug) ?? null
      const languageName = languageDefinition?.name ?? languageSlug.toUpperCase()
      const languageFlag = languageDefinition?.flag?.trim() || null
      const nextExtra: Record<string, unknown> = {
        ...(current?.extra_config ?? {}),
        ...(payload.extra_config ?? {}),
      }

      if (languageFlag) {
        nextExtra.flag = languageFlag
      } else {
        delete nextExtra.flag
      }

      const updated = {
        ...applyRealizationPayload(project, current, payload),
        name: languageName,
        slug: languageSlug,
        language: languageSlug,
        extra_config: nextExtra,
      }
      const nextRealizations = project.realizations.some((real) => real.slug === languageSlug)
        ? project.realizations.map((real) => (real.slug === languageSlug ? updated : real))
        : [...project.realizations, updated]

      return [{ ...project, realizations: nextRealizations }, ...projects.slice(1)]
    })
  }

  const openProjectOverlay = (project: TemplateProject | null = null) => {
    const payload: ProjectTemplatePayload | null = project
      ? {
          key: project.key != null ? String(project.key) : null,
          name: project.name,
          slug: project.slug,
          description: project.description,
          status: project.status,
          locked: Boolean(project.locked),
          widget_config: project.widget_config,
          session_config: normalizeSessionConfig(
            project.session_config ?? null,
            SESSION_DEFAULTS_INACTIVE,
          ),
        }
      : null

    openOverlay({
      title: project ? "Projekt-Vorlage bearbeiten" : "Projekt-Vorlage anlegen",
      content: ProvisioningTemplateOverlay,
      properties: {
        mode: "project",
        project: payload ?? undefined,
        defaultName: pc.graph?.label ?? "Projekt",
        hasVoiceFeature,
        hasChatFeature,
        onSubmit: (value: ProjectTemplatePayload) => handleProjectSubmit(project, value),
      },
    })
  }

  const openProjectTemplateOverlay = () => {
    if (pc.saving || pc.synchronizing) return

    if (!shouldShowTemplate) return

    let project = projectTemplates[0] ?? null

    if (!project) {
      const baseName = pc.graph?.label ?? "Projekt"
      applyProjects(() => [createDefaultProjectTemplate(baseName)])
      project = projectTemplates[0] ?? null
    }

    openProjectOverlay(project)
  }

  const openLanguageOverlay = (languageSlug: string) => {
    if (pc.saving || pc.synchronizing) return

    if (!shouldShowTemplate) return

    let project = projectTemplates[0] ?? null

    if (!project) {
      const baseName = pc.graph?.label ?? "Projekt"
      applyProjects(() => [createDefaultProjectTemplate(baseName)])
      project = projectTemplates[0] ?? null
    }

    if (!project) return

    const realization = project.realizations.find((real) => real.slug === languageSlug) ?? null
    const languageDefinition =
      graphRealizations.find((entry) => entry.slug === languageSlug) ?? null
    const languageName = languageDefinition?.name ?? languageSlug.toUpperCase()
    const languageFlag = languageDefinition?.flag?.trim() || null
    const languageLabel = languageFlag ? `${languageFlag} ${languageName}` : languageName

    const payload: RealizationTemplatePayload | null = realization
      ? {
          key: realization.key != null ? String(realization.key) : null,
          name: realization.name,
          slug: realization.slug,
          status: realization.status,
          description: realization.description,
          language: realization.language,
          locked: Boolean(realization.locked),
          allowed_origins: [...(realization.allowed_origins ?? [])],
          extra_config: { ...(realization.extra_config ?? {}) },
          widget_config: realization.widget_config ?? null,
          session_config: normalizeSessionConfig(
            realization.session_config ?? null,
            SESSION_DEFAULTS_INACTIVE,
          ),
        }
      : null

    openOverlay({
      title: `${languageLabel} bearbeiten`,
      content: ProvisioningTemplateOverlay,
      properties: {
        mode: "realization",
        projectName: project.name,
        realization: payload ?? undefined,
        languageOptions: [{ value: languageSlug, name: languageLabel }],
        defaultLanguage: languageSlug,
        hasVoiceFeature,
        hasChatFeature,
        onSubmit: (value: RealizationTemplatePayload) =>
          handleRealizationSubmit(languageSlug, value),
      },
    })
  }
</script>

<div class="graph-settings form">
  {#if loadError && !pc.loading}
    <p class="hint error">{loadError}</p>
  {/if}
  <SectionTitle
    title="Vorkonfiguration"
    brightBackground={true}
    helpModalTextId="graphDetailProvisioning"
    styleOuter="margin-bottom: 1.2rem;"
  >
    {#snippet buttonSnippet()}
      <ToggleCTA
        options={AUTO_APPLY_TOGGLE_OPTIONS}
        bind:value={autoApplySelection}
        darkMode={false}
        onChange={handleAutoApplyToggle}
      />
      <CTA
        type="secondaryButton"
        content="Synchronize"
        onClick={handleSynchronizeClick}
        icon={mdiSync}
        disabled={pc.saving ||
          pc.synchronizing ||
          !pc.draft ||
          !shouldShowTemplate ||
          !templateValid}
      />
      <CTA
        type="secondaryButton"
        content="Bearbeiten"
        icon={mdiPencil}
        onClick={openProjectTemplateOverlay}
        disabled={pc.saving || pc.synchronizing || !pc.draft || !shouldShowTemplate}
      />
    {/snippet}
  </SectionTitle>

  <div class="provisioning-body">
    <div class="template-shell" data-disabled={!shouldShowTemplate}>
      <div class="template-body">
        <Table
          rowActions={{
            resolve: (row, rowIndex) =>
              buildTableRowActionSet(
                ((row) => {
                  const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] =
                    []

                  const r = row as LanguageRow

                  actions.push({
                    id: "action-0",

                    label: "Sprache bearbeiten",
                    onClick: () => {
                      openLanguageOverlay(r.slug)
                    },
                    icon: mdiPencil,
                    disabled: pc.saving || pc.synchronizing || !pc.draft,
                    tooltip: "Sprache bearbeiten",
                  })

                  return actions
                })(row) ?? [],
                { primaryId: "action-0", inlineIds: [] },
              ),
          }}
          columns={languageColumns}
          getRows={getLanguageRows}
          refreshKey={languageRefreshKey}
          loading={pc.loading}
          theme="light"
          mode="table"
          showActions={true}
          actionsHeaderLabel="Aktionen"
        >
          {#snippet tableCell({ row, column })}
            {@const r = row as LanguageRow}
            {#if column.id === "name"}
              {r.flag ? `${r.flag} ${r.name}` : r.name}
            {:else}
              {r[column.id] as any}
            {/if}
          {/snippet}

          {#snippet empty()}
            <p class="muted">Keine Sprachen definiert.</p>
          {/snippet}
        </Table>
      </div>
    </div>
  </div>
</div>

<style lang="less">
  @import "./provisioningTab.less";

  .template-shell {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .template-shell[data-disabled="true"] .template-body {
    pointer-events: none;
    opacity: 0.5;
    filter: saturate(0.5);
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
  }

  .hint.error {
    color: var(--danger-600, #c62828);
  }
</style>
